import { useState } from 'react';
import { Loader2, Send, CheckCircle2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { submitQuote } from '../lib/api';
import { SERVICES } from '../lib/site';

const initialForm = {
    name: '', email: '', phone: '', service: '',
    property_size: '', postcode: '', message: '',
};

export const QuoteDialog = ({ open, onOpenChange, defaultService }) => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleChange = (key) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const handleClose = (next) => {
        onOpenChange(next);
        if (!next) {
            setTimeout(() => { setForm(initialForm); setDone(false); }, 250);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.service) {
            toast.error('Please fill in name, email, phone and service.');
            return;
        }
        setLoading(true);
        try {
            await submitQuote(form);
            setDone(true);
            toast.success('Quote request sent — we\'ll reply within 1 hour.');
        } catch (err) {
            toast.error('Could not send your request. Please try again or call us.');
        } finally {
            setLoading(false);
        }
    };

    // Allow opening with a pre-selected service
    if (open && defaultService && !form.service) {
        setTimeout(() => setForm((f) => ({ ...f, service: defaultService })), 0);
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="quote-form"
                className="sm:max-w-[560px] p-0 overflow-hidden border-border rounded-2xl"
            >
                <button
                    onClick={() => handleClose(false)}
                    className="absolute right-4 top-4 z-10 grid place-items-center w-8 h-8 rounded-full bg-white/80 backdrop-blur hover:bg-white border border-border"
                    aria-label="Close"
                    data-testid="quote-form-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {done ? (
                    <div className="p-10 text-center flex flex-col items-center gap-4" data-testid="quote-success">
                        <span className="grid place-items-center w-14 h-14 rounded-full bg-accent/20 text-primary">
                            <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
                        </span>
                        <DialogHeader className="text-center">
                            <DialogTitle className="font-outfit text-2xl">Thank you</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                Your quote request has been sent. A member of our team will reply within the hour during working hours.
                            </DialogDescription>
                        </DialogHeader>
                        <Button onClick={() => handleClose(false)} className="rounded-full bg-primary text-primary-foreground mt-2">
                            Close
                        </Button>
                    </div>
                ) : (
                    <div className="p-7 md:p-9">
                        <DialogHeader>
                            <span className="overline">Request a quote</span>
                            <DialogTitle className="font-outfit text-2xl mt-2">Tell us about your space</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                Free, no-obligation quote. Reply within 1 hour, Mon–Sat.
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="q-name" className="text-xs text-muted-foreground">Full name</Label>
                                    <Input id="q-name" data-testid="quote-input-name" value={form.name} onChange={handleChange('name')} placeholder="Eleanor Ross" required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="q-email" className="text-xs text-muted-foreground">Email</Label>
                                    <Input id="q-email" data-testid="quote-input-email" type="email" value={form.email} onChange={handleChange('email')} placeholder="you@email.com" required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="q-phone" className="text-xs text-muted-foreground">Phone</Label>
                                    <Input id="q-phone" data-testid="quote-input-phone" value={form.phone} onChange={handleChange('phone')} placeholder="07xxx xxxxxx" required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-muted-foreground">Service</Label>
                                    <Select value={form.service} onValueChange={handleChange('service')}>
                                        <SelectTrigger data-testid="quote-input-service">
                                            <SelectValue placeholder="Select a service" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SERVICES.map((s) => (
                                                <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="q-size" className="text-xs text-muted-foreground">Property size</Label>
                                    <Input id="q-size" data-testid="quote-input-size" value={form.property_size} onChange={handleChange('property_size')} placeholder="e.g. 2-bed flat" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="q-postcode" className="text-xs text-muted-foreground">Postcode</Label>
                                    <Input id="q-postcode" data-testid="quote-input-postcode" value={form.postcode} onChange={handleChange('postcode')} placeholder="BS8 4AA" />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="q-msg" className="text-xs text-muted-foreground">Anything else?</Label>
                                <Textarea id="q-msg" data-testid="quote-input-message" value={form.message} onChange={handleChange('message')} rows={3} placeholder="Tell us about access, pets, or your timeline." />
                            </div>

                            <Button
                                type="submit"
                                data-testid="quote-form-submit"
                                disabled={loading}
                                className="w-full rounded-full bg-primary text-primary-foreground font-outfit h-12 text-sm hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(17,47,34,0.55)] transition-all"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send my request</>}
                            </Button>

                            <p className="text-[11px] text-muted-foreground text-center pt-1">
                                Submitting sends us an email — we never share your details.
                            </p>
                        </form>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
